/* eslint-disable react/no-multi-comp */

import copy from 'clipboard-copy';
import Quill from 'quill';
import React, { useEffect, useRef, useState } from 'react';

import {
    Icon,
    IconCopy,
    IconDeviceDocument,
    IconDownload,
    IconTrash
} from '../../../base/icons';

const UndoIcon = () => (
    <svg
        fill = 'none'
        height = '14'
        stroke = 'currentColor'
        strokeLinecap = 'round'
        strokeLinejoin = 'round'
        strokeWidth = '2'
        viewBox = '0 0 24 24'
        width = '14'>
        <path d = 'M3 7v6h6' />
        <path d = 'M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13' />
    </svg>
);

const RedoIcon = () => (
    <svg
        fill = 'none'
        height = '14'
        stroke = 'currentColor'
        strokeLinecap = 'round'
        strokeLinejoin = 'round'
        strokeWidth = '2'
        viewBox = '0 0 24 24'
        width = '14'>
        <path d = 'M21 7v6h-6' />
        <path d = 'M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13' />
    </svg>
);

const ClockIcon = () => (
    <svg
        fill = 'none'
        height = '13'
        stroke = 'currentColor'
        strokeLinecap = 'round'
        strokeLinejoin = 'round'
        strokeWidth = '2'
        viewBox = '0 0 24 24'
        width = '13'>
        <circle
            cx = '12'
            cy = '12'
            r = '10' />
        <polyline points = '12 6 12 12 16 14' />
    </svg>
);

const Link = Quill.import('formats/link');
const Delta = Quill.import('delta');

class CustomLink extends Link {
    static create(value) {
        const node = super.create(value);
        let href = value;

        if (typeof href === 'string') {
            if (!/^(https?:\/\/|mailto:|tel:|\/)/i.test(href)) {
                href = `https://${href}`;
            }
        }
        node.setAttribute('href', this.sanitize(href));
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');

        return node;
    }
}
Quill.register(CustomLink, true);

/**
 * Converts raw plain text containing URLs into Quill Delta ops with link formatting.
 *
 * @param {string} rawText - Raw text to convert.
 * @returns {Array<Object>} Quill Delta ops.
 */
export function textToOpsWithLinks(rawText) {
    if (!rawText) {
        return [];
    }
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    if (!urlRegex.test(rawText)) {
        return [ { insert: rawText } ];
    }
    const ops = [];
    let lastIndex = 0;

    urlRegex.lastIndex = 0;
    let match;

    while ((match = urlRegex.exec(rawText)) !== null) {
        if (match.index > lastIndex) {
            ops.push({ insert: rawText.slice(lastIndex, match.index) });
        }
        ops.push({
            insert: match[0],
            attributes: { link: match[0] }
        });
        lastIndex = urlRegex.lastIndex;
    }
    if (lastIndex < rawText.length) {
        ops.push({ insert: rawText.slice(lastIndex) });
    }

    return ops;
}

/**
 * Handles clicks inside notes so links open in a new tab safely.
 *
 * @param {MouseEvent} event - Click event.
 * @returns {void}
 */
export function handleNotesLinkClick(event) {
    const link = event.target?.closest?.('a');

    if (link && link.href) {
        if (link.classList?.contains('ql-action') || link.classList?.contains('ql-remove')) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        window.open(link.href, '_blank', 'noopener,noreferrer');

        return;
    }

    try {
        let range;

        if (document.caretRangeFromPoint) {
            range = document.caretRangeFromPoint(event.clientX, event.clientY);
        } else if (document.caretPositionFromPoint) {
            const pos = document.caretPositionFromPoint(event.clientX, event.clientY);

            if (pos) {
                range = document.createRange();
                range.setStart(pos.offsetNode, pos.offset);
            }
        }

        if (range && range.startContainer && range.startContainer.nodeType === Node.TEXT_NODE) {
            const textContent = range.startContainer.textContent;
            const offset = range.startOffset;
            const left = textContent.slice(0, offset).search(/[^\s]+$/);
            const rightMatch = textContent.slice(offset).match(/^[^\s]+/);

            if (left !== -1 && rightMatch) {
                const word = textContent.slice(left, offset + rightMatch[0].length).replace(/[.,;:!?)\]]+$/, '');

                if (/^https?:\/\/[^\s]+$/i.test(word)) {
                    event.preventDefault();
                    event.stopPropagation();
                    window.open(word, '_blank', 'noopener,noreferrer');
                }
            }
        }
    } catch {
        // Ignore fallback errors
    }
}

// font/size/color/background/align have no toolbar controls any more but stay
// allowed so notes written with the old toolbar still render.
export const FORMATS = [
    'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'code',
    'color', 'background', 'list', 'indent', 'align', 'link', 'blockquote', 'code-block'
];

// Shown twice: pinned above the page and floating over a selection.
const TOOLBAR = [
    [ { header: 1 }, { header: 2 } ],
    [ 'bold', 'italic', 'strike', 'code', 'link' ],
    [ { list: 'bullet' }, { list: 'ordered' }, { list: 'check' } ],
    [ 'blockquote', 'code-block', 'clean' ]
];

// The pinned bar keeps the everyday formats; the rest stay on the selection
// bar and the markdown shortcuts.
const PINNED_TOOLBAR = [
    [ { header: 1 }, { header: 2 } ],
    [ 'bold', 'italic', 'strike', 'link' ],
    [ { list: 'bullet' }, { list: 'check' } ]
];

// Start-of-line shortcuts shown while the notes are empty.
const SHORTCUTS = [
    [ '#', 'header_1' ],
    [ '-', 'list_bullet' ],
    [ '[]', 'list_check' ],
    [ '>', 'blockquote' ]
];

/**
 * Markdown-style block shortcuts typed at the start of a line, then space:
 * `#`/`##`/`###` heading, `>` quote, three backticks code block. Quill's own
 * "list autofill" binding already covers `-`, `*`, `1.`, `[]` and `[x]`.
 */
const MARKDOWN_BLOCK_SHORTCUT = {
    key: ' ',
    collapsed: true,
    format: { 'code-block': false },
    prefix: /^\s*?(#{1,3}|>|```)$/,
    handler(range, { prefix }) {
        const mark = prefix.trim();
        const start = range.index - prefix.length;
        const [ name, value ] = mark === '>' ? [ 'blockquote', true ]
            : mark === '```' ? [ 'code-block', true ] : [ 'header', mark.length ];

        this.quill.history.cutoff();
        this.quill.deleteText(start, prefix.length, 'user');
        this.quill.formatLine(start, 1, name, value, 'user');
        this.quill.history.cutoff();

        return false;
    }
};

/**
 * @typedef {Object} Props
 * @property {React$Node} [children] - Save status, shown in the footer.
 * @property {Function} onChange - (text, richText) change callback.
 * @property {boolean} [readOnly] - Locks the editor when true.
 * @property {Object|null} [richText] - Quill document, if any.
 * @property {string} roomName - Room owning the notes.
 * @property {Function} t - Translation function.
 * @property {string} text - Plain notes text.
 */
/**
 * Device-local rich editor. Quill owns its DOM; React owns the surrounding UI.
 * With {@code readOnly} every mutation control is disabled and Quill stops
 * accepting edits, while copy and download keep working.
 *
 * @param {Props} props - Document and change callback.
 * @returns {React$Element}
 */
export default function RichNotesEditor({ children, onChange, readOnly = false, richText, roomName, t, text }) {
    const host = useRef(null);
    const toolbarSlot = useRef(null);
    const editor = useRef(null);
    const latest = useRef({ onChange,
        readOnly,
        richText,
        text });
    const [ confirmClear, setConfirmClear ] = useState(false);
    const [ feedback, setFeedback ] = useState('');
    const [ downloadOpen, setDownloadOpen ] = useState(false);
    const downloadRef = useRef(null);
    const [ history, setHistory ] = useState({ undo: false,
        redo: false });

    useEffect(() => {
        if (!downloadOpen) {
            return;
        }

        const handleOutsideClick = event => {
            if (downloadRef.current && !downloadRef.current.contains(event.target)) {
                setDownloadOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
        };
    }, [ downloadOpen ]);

    useEffect(() => {
        if (!feedback) {
            return;
        }

        const timer = setTimeout(() => {
            setFeedback('');
        }, 3000);

        return () => clearTimeout(timer);
    }, [ feedback ]);

    latest.current = { onChange,
        readOnly,
        richText,
        text };

    useEffect(() => {
        const mount = host.current;
        const slot = toolbarSlot.current;
        const container = document.createElement('div');

        mount.appendChild(container);
        const quill = new Quill(container, {
            bounds: host.current,
            formats: FORMATS,
            modules: {
                history: { delay: 600,
                    maxStack: 100,
                    userOnly: true },
                keyboard: { bindings: { markdownBlock: MARKDOWN_BLOCK_SHORTCUT } },

                // Bubble theme: this toolbar floats over the selection.
                toolbar: TOOLBAR
            },
            placeholder: t('notesPane.placeholder'),
            theme: 'bubble'
        });

        editor.current = quill;

        // Auto-format pasted plain-text URLs into clickable link ops
        quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
            const raw = node.data;
            const urlRegex = /(https?:\/\/[^\s]+)/g;

            if (!urlRegex.test(raw)) {
                return delta;
            }
            const newDelta = new Delta();
            let lastIndex = 0;

            urlRegex.lastIndex = 0;
            let match;

            while ((match = urlRegex.exec(raw)) !== null) {
                if (match.index > lastIndex) {
                    newDelta.insert(raw.slice(lastIndex, match.index));
                }
                newDelta.insert(match[0], { link: match[0] });
                lastIndex = urlRegex.lastIndex;
            }
            if (lastIndex < raw.length) {
                newDelta.insert(raw.slice(lastIndex));
            }

            return newDelta;
        });

        quill.root.addEventListener('click', handleNotesLinkClick);

        quill.root.setAttribute('role', 'textbox');
        quill.root.setAttribute('aria-label', t('notesPane.title'));
        quill.root.setAttribute('aria-multiline', 'true');
        quill.root.setAttribute('spellcheck', 'true');
        if (latest.current.richText) {
            quill.setContents(latest.current.richText);
        } else if (latest.current.text) {
            const ops = textToOpsWithLinks(latest.current.text);

            quill.setContents({ ops });
        } else {
            quill.setText('');
        }
        quill.history.clear();
        quill.enable(!latest.current.readOnly);
        quill.root.setAttribute('aria-readonly', String(latest.current.readOnly));

        // Second, pinned copy above the page. Bubble's handlers (checklist toggle,
        // link editor) and icons are reused so both toolbars behave the same.
        const Toolbar = Quill.import('modules/toolbar');
        const pinned = new Toolbar(quill, { container: PINNED_TOOLBAR,
            handlers: quill.getModule('toolbar').handlers });

        pinned.container.classList.add('ql-bubble');
        quill.theme.buildButtons(pinned.container.querySelectorAll('button'), Quill.import('ui/icons'));

        // Quill inserts it in the page; it lives in the sticky row with undo/redo.
        slot.appendChild(pinned.container);
        const toolbars = [ ...mount.querySelectorAll('.ql-toolbar'), pinned.container ];

        toolbars.forEach(toolbar => {
            toolbar.setAttribute('role', 'group');
            toolbar.setAttribute('aria-label', t('notesPane.formatting'));
        });
        toolbars.flatMap(toolbar => [ ...toolbar.querySelectorAll('button') ]).forEach(control => {
            const name = Array.from(control.classList)
                .find(item => item.startsWith('ql-'))
                ?.slice(3);
            const value = control.value;
            const detail = value && [ 'header', 'list' ].includes(name)
                ? t(`notesPane.formats.${name}_${value}`) : t(`notesPane.formats.${name}`);

            control.setAttribute('aria-label', detail);
            control.setAttribute('title', detail);
            control.setAttribute('type', 'button');
        });

        const handleChange = () => {
            const nextText = quill.getText().slice(0, -1);

            latest.current.onChange(nextText, nextText ? { ops: quill.getContents().ops } : null);
            setHistory({ undo: quill.history.stack.undo.length > 0,
                redo: quill.history.stack.redo.length > 0 });
            setFeedback('');
        };

        quill.on('text-change', handleChange);

        return () => {
            quill.root.removeEventListener('click', handleNotesLinkClick);
            quill.off('text-change', handleChange);
            editor.current = null;
            mount.replaceChildren();
            slot.replaceChildren();
        };
    }, [ t ]);

    // The conference renders two responsive pane instances; keep both in sync
    // without resetting the selection or undo history of the active editor.
    useEffect(() => {
        const quill = editor.current;

        if (!quill) {
            return;
        }
        const changed = richText
            ? JSON.stringify(quill.getContents().ops) !== JSON.stringify(richText.ops)
            : quill.getText().slice(0, -1) !== text;

        if (changed) {
            // A collaborator's edit must not throw our caret to the end.
            const selection = quill.hasFocus() ? quill.getSelection() : null;
            const before = quill.getContents();

            if (richText) {
                quill.setContents(richText, 'silent');
            } else if (text) {
                const ops = textToOpsWithLinks(text);

                quill.setContents({ ops }, 'silent');
            } else {
                quill.setText('', 'silent');
            }
            if (selection) {
                const index = before.diff(quill.getContents()).transformPosition(selection.index);

                quill.setSelection(index, selection.length, 'silent');
            }
            quill.history.clear();
            setHistory({ undo: false,
                redo: false });
        }
    }, [ richText, text ]);

    // Lock the whole editor while a submission is in flight or the view is
    // read-only: typing, formatting, history and insert/clear all stop, while
    // copy and download keep working.
    useEffect(() => {
        const quill = editor.current;

        if (!quill) {
            return;
        }
        quill.enable(!readOnly);
        quill.root.setAttribute('aria-readonly', String(readOnly));
    }, [ readOnly ]);

    const insertText = value => {
        const quill = editor.current;
        const index = quill.getSelection()?.index ?? quill.getLength() - 1;

        quill.history.cutoff();
        quill.insertText(index, value, 'user');
        quill.setSelection(index + value.length, 0);
        quill.history.cutoff();
    };
    const insertTemplate = () => {
        const quill = editor.current;
        const index = quill.getLength() - 1;
        const ops = text ? [ { retain: index }, { insert: '\n' } ] : [];

        for (const section of [ 'discussion', 'decisions', 'actions' ]) {
            ops.push({ insert: t(`notesPane.${section}`) }, { insert: '\n',
                attributes: { header: 2 } },
                { insert: '\n',
                    attributes: section === 'actions' ? { list: 'unchecked' } : {} });
        }
        quill.history.cutoff();
        quill.updateContents({ ops }, 'user');
        quill.setSelection(index, 0);
        quill.history.cutoff();
    };
    const copyNotes = async () => {
        try {
            await copy(editor.current.getText().slice(0, -1));
            setFeedback(t('notesPane.copied'));
        } catch {
            setFeedback(t('notesPane.copyFailed'));
        }
    };
    const download = extension => {
        const quill = editor.current;
        const content = extension === 'txt' ? quill.getText().slice(0, -1)
            : '<!doctype html><html><head><meta charset="utf-8"><title>Notes</title><style>'
                + 'body{max-width:800px;margin:40px auto;padding:24px;font:16px/1.7 system-ui}'
                + 'blockquote{border-left:3px solid #ccc;padding-left:16px}pre{white-space:pre-wrap}'
                + 'li[data-list="checked"]{list-style-type:"☑ "}li[data-list="unchecked"]{list-style-type:"☐ "}'
                + '.ql-align-center{text-align:center}.ql-align-right{text-align:right}.ql-align-justify{text-align:justify}'
                + '.ql-font-serif{font-family:Georgia,serif}.ql-font-monospace{font-family:monospace}'
                + '.ql-size-small{font-size:.75em}.ql-size-large{font-size:1.5em}.ql-size-huge{font-size:2.5em}'
                + `</style></head><body>${quill.getSemanticHTML()}</body></html>`;
        const url = URL.createObjectURL(new Blob([ content ], { type: extension === 'txt' ? 'text/plain;charset=utf-8' : 'text/html;charset=utf-8' }));
        const link = document.createElement('a');

        link.href = url;
        link.download = `notes-${(roomName || 'meeting').replace(/[^a-z0-9_-]/gi, '_').slice(0, 80)}.${extension}`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setFeedback(t('notesPane.downloaded'));
    };
    const runHistory = action => {
        editor.current.history[action]();
        setHistory({ undo: editor.current.history.stack.undo.length > 0,
            redo: editor.current.history.stack.redo.length > 0 });
    };

    return (
        <>
            <div className = { `notes_pane-format-row${readOnly ? ' notes_pane-format-row--readonly' : ''}` }>
                <div
                    className = 'notes_pane-format-slot'
                    ref = { toolbarSlot } />
                <div
                    aria-label = 'History'
                    className = 'notes_pane-history'
                    role = 'group'>
                    <button
                        aria-label = { t('notesPane.undo') }
                        disabled = { readOnly || !history.undo }
                        onClick = { () => runHistory('undo') }
                        title = { t('notesPane.undo') }
                        type = 'button'>
                        <UndoIcon />
                    </button>
                    <button
                        aria-label = { t('notesPane.redo') }
                        disabled = { readOnly || !history.redo }
                        onClick = { () => runHistory('redo') }
                        title = { t('notesPane.redo') }
                        type = 'button'>
                        <RedoIcon />
                    </button>
                </div>
            </div>
            <div
                className = { `notes_pane-paper${readOnly ? ' notes_pane-paper--readonly' : ''}` }
                ref = { host } />
            {!text && !readOnly && (
                <div className = 'notes_pane-shortcuts'>
                    <p className = 'notes_pane-shortcuts-title'>{t('notesPane.formatting')}</p>
                    <ul>
                        {SHORTCUTS.map(([ key, format ]) => (
                            <li key = { key }>
                                <kbd>{key}</kbd>
                                <span>{t(`notesPane.formats.${format}`).replace(/\s*\(.*\)$/, '')}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <div
                aria-label = { t('notesPane.insert') }
                className = 'notes_pane-insert'
                role = 'group'>
                <button
                    className = 'notes_pane-chip'
                    disabled = { readOnly }
                    onClick = { insertTemplate }
                    type = 'button'>
                    <Icon
                        size = { 15 }
                        src = { IconDeviceDocument } />
                    <span>{t('notesPane.template')}</span>
                </button>
                <button
                    className = 'notes_pane-chip'
                    disabled = { readOnly }
                    onClick = { () => insertText(`${new Date().toLocaleTimeString([], { hour: '2-digit',
                        minute: '2-digit' })} — `) }
                    type = 'button'>
                    <ClockIcon />
                    <span>{t('notesPane.timestamp')}</span>
                </button>
            </div>
            <div className = 'notes_pane-footer'>
                <div className = 'notes_pane-footer-meta'>
                    {children}
                    <span className = 'notes_pane-counts'>
                        {t('notesPane.wordCount', { count: text.trim() ? text.trim().split(/\s+/u).length : 0 })}
                        {' · '}
                        {t('notesPane.characterCount', { count: Array.from(text).length })}
                    </span>
                </div>
                <div className = 'notes_pane-footer-actions'>
                    <button
                        aria-label = { t('notesPane.copy') }
                        disabled = { !text }
                        onClick = { copyNotes }
                        title = { t('notesPane.copy') }
                        type = 'button'>
                        <Icon
                            size = { 18 }
                            src = { IconCopy } />
                    </button>
                    <div
                        className = 'notes_pane-download-wrapper'
                        ref = { downloadRef }>
                        <button
                            aria-expanded = { downloadOpen }
                            aria-haspopup = 'true'
                            aria-label = { t('notesPane.download') }
                            className = { downloadOpen ? 'is-active' : '' }
                            disabled = { !text }
                            onClick = { () => setDownloadOpen(!downloadOpen) }
                            title = { t('notesPane.download') }
                            type = 'button'>
                            <Icon
                                size = { 18 }
                                src = { IconDownload } />
                        </button>
                        {downloadOpen && (
                            <div
                                className = 'notes_pane-download-menu'
                                role = 'menu'>
                                <button
                                    className = 'notes_pane-download-item'
                                    onClick = { () => {
                                        download('txt');
                                        setDownloadOpen(false);
                                    } }
                                    role = 'menuitem'
                                    type = 'button'>
                                    <span className = 'notes_pane-download-badge'>TXT</span>
                                    <span>{t('notesPane.downloadText')}</span>
                                </button>
                                <button
                                    className = 'notes_pane-download-item'
                                    onClick = { () => {
                                        download('html');
                                        setDownloadOpen(false);
                                    } }
                                    role = 'menuitem'
                                    type = 'button'>
                                    <span className = 'notes_pane-download-badge'>HTML</span>
                                    <span>{t('notesPane.downloadHtml')}</span>
                                </button>
                            </div>
                        )}
                    </div>
                    <button
                        aria-label = { t('notesPane.clear') }
                        className = 'notes_pane-clear'
                        disabled = { readOnly || !text }
                        onClick = { () => setConfirmClear(true) }
                        title = { t('notesPane.clear') }
                        type = 'button'>
                        <Icon
                            size = { 18 }
                            src = { IconTrash } />
                    </button>
                </div>
            </div>
            {confirmClear && (
                <div
                    aria-label = { t('notesPane.clear') }
                    className = 'notes_pane-confirm'
                    role = 'group'>
                    <p>{t('notesPane.confirmClear')}</p>
                    <div className = 'notes_pane-confirm-actions'>
                        <button
                            autoFocus = { true }
                            onClick = { () => setConfirmClear(false) }
                            type = 'button'>{t('notesPane.cancel')}</button>
                        <button
                            className = 'notes_pane-danger'
                            disabled = { readOnly }
                            onClick = { () => {
                                editor.current.history.cutoff();
                                editor.current.setText('', 'user');
                                setConfirmClear(false);
                                editor.current.focus();
                            } }
                            type = 'button'>{t('notesPane.clear')}</button>
                    </div>
                </div>
            )}
            <span
                aria-live = 'polite'
                className = { `notes_pane-feedback${feedback ? ' is-visible' : ''}` }
                role = 'status'>{feedback}</span>
        </>
    );
}
